import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/modules/auth/presentation/hooks/use-auth';
import {
  useAuthModal,
  type AuthReason,
  type AuthView,
} from '@/modules/auth/presentation/components/AuthModal';
import { ProfileRequiredError } from '@/modules/auth/domain/entities/auth.entity';
import type { SubmissionKind } from '../../domain/entities/submission.entity';
import { track } from '@/lib/analytics';
import { useMySubmission, useSubmit } from './use-submissions';

type Payload = Record<string, number>;

/**
 * Enviar algo que exige conta — o ensaio ou o gabarito:
 *
 * - **com sessão**, envia direto;
 * - **sem sessão**, abre o diálogo de conta no passo `firstView` e envia assim
 *   que a pessoa entra. As respostas esperam numa ref, não no estado: não há o
 *   que renderizar com elas;
 * - **com a sessão ainda carregando** (o login real leva centenas de
 *   milissegundos para restaurar), espera ela terminar antes de decidir. Sem
 *   isso, quem já está logado e clica rápido seria perguntado se tem conta;
 * - **com sessão, mas sem perfil nesta plataforma** (conta da Primum de outra
 *   plataforma), abre o cadastro curto e envia assim que ele termina. O mesmo
 *   vale se o back responder 403 a uma sessão que parecia completa.
 */
export function useAuthenticatedSubmit({
  kind,
  reason,
  firstView,
  onDone,
}: {
  kind: SubmissionKind;
  reason: AuthReason;
  firstView: AuthView;
  onDone: () => void;
}) {
  const { user, isRestoring, markProfileMissing } = useAuth();
  const submit = useSubmit(kind);
  const mine = useMySubmission(kind);
  const waiting = useRef<Payload | null>(null);
  const [isWaitingSession, setIsWaitingSession] = useState(false);

  /**
   * Os eventos de conversão saem **no sucesso**, nunca no clique: o clique
   * conta envio que falhou e o de quem ainda nem tem sessão.
   */
  const send = (payload: Payload) => {
    const respondidas = Object.keys(payload).length;
    // Lido antes do envio: depois dele a leitura já mostra o envio novo. Sem
    // leitura (quem acabou de entrar), não dá para saber.
    const tipo_envio = mine.data
      ? 'reenvio'
      : mine.isSuccess
        ? 'primeiro'
        : 'desconhecido';

    submit.mutate(payload, {
      onSuccess: () => {
        if (kind === 'ensaio') {
          track({ event: 'ensaio_enviado', respondidas });
        } else {
          track({ event: 'gabarito_enviado', respondidas, tipo_envio });
        }
        onDone();
      },
      onError: (error) => {
        if (error instanceof ProfileRequiredError) {
          // Não é falha de envio: falta o cadastro daqui. As respostas
          // esperam e saem quando ele terminar.
          markProfileMissing();
          waiting.current = payload;
          authModal.open({ kind: 'complete-profile' });
          return;
        }
        track({ event: 'envio_erro', tipo: kind });
      },
    });
  };

  const authModal = useAuthModal(reason, () => {
    if (waiting.current) send(waiting.current);
    waiting.current = null;
  });

  const route = (payload: Payload) => {
    if (user && !user.hasProfile) {
      waiting.current = payload;
      authModal.open({ kind: 'complete-profile' });
      return;
    }
    if (user) {
      send(payload);
      return;
    }
    waiting.current = payload;
    authModal.open(firstView);
  };

  useEffect(() => {
    if (!isWaitingSession || isRestoring) return;
    setIsWaitingSession(false);
    const payload = waiting.current;
    waiting.current = null;
    if (payload) route(payload);
    // `route` lê o `user` deste render, que já é o restaurado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWaitingSession, isRestoring]);

  const finish = (payload: Payload) => {
    if (isRestoring) {
      waiting.current = payload;
      setIsWaitingSession(true);
      return;
    }
    route(payload);
  };

  return {
    finish,
    authModalProps: authModal.modalProps,
    isSubmitting: submit.isPending || isWaitingSession,
    isError: submit.isError && !(submit.error instanceof ProfileRequiredError),
  };
}
